import { FormikErrors } from 'formik';
import i18next from 'i18next';
import toast from 'react-hot-toast';
import {
  IErrorResponse,
  IErrorsResponse,
} from 'types/service/errorCodes.interface';

type OutputObject = {
  [key: string]: any;
};

const normalizeErrorProperty = (property: string | null): string[] => {
  if (!property) {
    return [];
  }

  const keys = property.split('.').filter(Boolean);

  if (keys.length > 1 && keys[0].toLowerCase() === 'license') {
    return keys.slice(1);
  }

  return keys;
};

const getErrorMessage = ({ errorCode, message }: IErrorResponse): string => {
  const translated = i18next.t(`error-code.${errorCode}`);

  if (translated !== `error-code.${errorCode}`) {
    return translated;
  }

  if (message?.trim()) {
    return message.trim();
  }

  return translated;
};

const transformResponseErrorsToFormikErrors = <Form>(
  errors: IErrorsResponse
): FormikErrors<Form> => {
  const result: OutputObject = {};

  errors.forEach(error => {
    const keys = normalizeErrorProperty(error.property);
    if (!keys.length) {
      return;
    }

    let currentLevel = result;

    keys.forEach((key, index) => {
      const keyLowerCase = `${key[0].toLowerCase()}${key.slice(1)}`;
      if (index === keys.length - 1) {
        currentLevel[keyLowerCase] = getErrorMessage(error);
      } else {
        if (!currentLevel[keyLowerCase]) {
          currentLevel[keyLowerCase] = {};
        }
        currentLevel = currentLevel[keyLowerCase];
      }
    });
  });

  return result as FormikErrors<Form>;
};

interface IHandleErrorsResponse<Form> {
  formik: FormikErrors<Form>;
  toasts: string[];
}

export const handleErrorsResponse = <Form>(
  errorMessage: string
): IHandleErrorsResponse<Form> => {
  let errors: IErrorsResponse = [];
  try {
    const parsed = JSON.parse(errorMessage);
    errors = Array.isArray(parsed) ? parsed : [];
  } catch {
    errors = [];
  }

  const errorsWithProperty = errors.filter(error => error.property !== null);
  const formikErrors =
    transformResponseErrorsToFormikErrors<Form>(errorsWithProperty);
  const toastErrors = errors.length ? errors.map(getErrorMessage) : [];

  return { formik: formikErrors, toasts: toastErrors };
};

export const onErrorShowAllErrorsToast = (
  error: Error,
  emptyToastsMessage?: string,
  maxErrorsDisplay = 3
) => {
  let errors: IErrorsResponse = [];
  try {
    const parsed = JSON.parse(error.message);
    errors = Array.isArray(parsed) ? parsed : [];
  } catch {
    errors = [];
  }
  errors.length
    ? errors.map(
        (error, key) =>
          key < maxErrorsDisplay && toast.error(getErrorMessage(error))
      )
    : emptyToastsMessage && toast.error(i18next.t(emptyToastsMessage));
};

export const onErrorShowToast = (error: Error, emptyToastsMessage?: string) => {
  const { toasts } = handleErrorsResponse(error.message);

  !!toasts.length
    ? toasts.map(message => toast.error(message))
    : emptyToastsMessage && toast.error(i18next.t(emptyToastsMessage));
};
